// ** Next Imports
import Link from 'next/link'
import { useRouter } from 'next/router'

// ** MUI Imports
import Chip from '@mui/material/Chip'
import ListItem from '@mui/material/ListItem'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemButton from '@mui/material/ListItemButton'

// ** Configs Import
import themeConfig from 'src/configs/themeConfig'

// ** Custom Components Imports
import UserIcon from 'src/layouts/components/UserIcon'
import Translations from 'src/layouts/components/Translations'
import CanViewNavLink from 'src/layouts/components/acl/CanViewNavLink'

// ** Util Import
import { handleURLQueries } from 'src/@core/layouts/utils'

// ** Styled Components
const MenuNavLink = styled(ListItemButton)(({ theme }) => ({
  width: '100%',
  borderRadius: 8,
  transition: 'padding-left .25s ease-in-out',
  '& .MuiTypography-root': {
    color: 'rgba(255,255,255,0.78)',
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  '& .MuiListItemIcon-root': {
    color: 'rgba(255,255,255,0.7)',
  },
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.10) !important',
    transition: 'none',
    '& .MuiTypography-root': { color: '#fff' },
    '& .MuiListItemIcon-root': { color: '#fff' },
  },
  '&.active': {
    backgroundColor: 'rgba(255,255,255,0.18)',
    borderLeft: '3px solid rgba(255,255,255,0.85)',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.22) !important',
    },
    '&.Mui-focusVisible': {
      backgroundColor: 'rgba(255,255,255,0.18)',
    },
    '& .MuiTypography-root': {
      fontWeight: 700,
      color: `${theme.palette.common.white} !important`,
    },
    '& .MuiListItemIcon-root': {
      color: `${theme.palette.common.white} !important`,
    },
  },
  // Submenu items (parent context) — dark text on light bg
  '&.sub-item': {
    '& .MuiTypography-root': {
      color: 'rgba(30,35,80,0.85)',
      fontWeight: 500,
    },
    '& .MuiListItemIcon-root': {
      color: '#2B388F',
    },
    '&:hover': {
      backgroundColor: 'rgba(43,56,143,0.08) !important',
      '& .MuiTypography-root': { color: '#1B205C' },
      '& .MuiListItemIcon-root': { color: '#1B205C' },
    },
    '&.active': {
      backgroundColor: 'transparent',
      borderLeft: 'none',
      '& .MuiTypography-root': { fontWeight: 700, color: '#1B205C !important' },
    },
  }
}))

const MenuItemTextMetaWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  justifyContent: 'space-between',
  transition: 'opacity .25s ease-in-out',
  ...(themeConfig.menuTextTruncate && { overflow: 'hidden' })
}))

const VerticalNavLink = ({
  item,
  parent,
  navHover,
  settings,
  navVisible,
  isSubToSub,
  collapsedNavWidth,
  toggleNavVisibility,
  navigationBorderWidth
}) => {
  // ** Hooks
  const router = useRouter()

  // ** Vars
  const { navCollapsed } = settings

  const icon = parent && !item.icon ? themeConfig.navSubItemIcon : item.icon
  
  // Show icons for main menu items or any submenu items that have icons
  const shouldShowIcon = !parent || item.icon

  const isNavLinkActive = () => {
    if (router.pathname === item.path || handleURLQueries(router, item.path)) {
      return true
    } else {
      return false
    }
  }

  return (
    <CanViewNavLink navLink={item}>
      <ListItem
        disablePadding
        className='nav-link'
        disabled={item.disabled || false}
        sx={{
          mt: 1.5,
          transition: 'padding .25s ease-in-out',
          px: theme => (parent ? '0 !important' : `${theme.spacing(navCollapsed && !navHover ? 2 : 3)} !important`)
        }}
      >
        <MenuNavLink
          component={Link}
          {...(item.disabled && { tabIndex: -1 })}
          className={[isNavLinkActive() ? 'active' : '', parent ? 'sub-item' : ''].filter(Boolean).join(' ')}
          href={item.path === undefined ? '/' : `${item.path}`}
          {...(item.openInNewTab ? { target: '_blank' } : null)}
          onClick={e => {
            if (item.path === undefined) {
              e.preventDefault()
              e.stopPropagation()
            }
            if (navVisible) {
              toggleNavVisibility()
            }
          }}
          sx={{
            py: 2.25,
            ...(item.disabled ? { pointerEvents: 'none' } : { cursor: 'pointer' }),
            pr: navCollapsed && !navHover ? (collapsedNavWidth - navigationBorderWidth - 24 - 16) / 8 : 3,
            pl: navCollapsed && !navHover ? (collapsedNavWidth - navigationBorderWidth - 24 - 16) / 8 : 4
          }}
        >
          {isSubToSub ? null : shouldShowIcon ? (
            parent && item.icon && item.iconBgColor ? (
              <Box
                sx={{
                  width: '32px',
                  height: '32px',
                  minWidth: '32px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '50%',
                  backgroundColor: item.iconBgColor,
                  color: item.iconColor,
                  ml: 2,
                  mr: 2,
                  transition: 'margin .25s ease-in-out',
                  '& svg': { fontSize: '1rem' },
                  ...(navCollapsed && !navHover ? { mr: 0, ml: 0 } : {})
                }}
              >
                <UserIcon icon={icon} />
              </Box>
            ) : (
              <ListItemIcon
                sx={{
                  transition: 'margin .25s ease-in-out',
                  color: parent ? 'text.secondary' : 'text.primary',
                  ...(navCollapsed && !navHover ? { mr: 0 } : { mr: 2 }),
                  ...(parent ? { ml: 2, mr: 4 } : {}),
                  '& svg': {
                    ...(!parent ? { fontSize: '1.5rem' } : { fontSize: '0.5rem' }),
                    ...(parent && item.icon ? { fontSize: '0.875rem' } : {})
                  }
                }}
              >
                <UserIcon icon={icon} />
              </ListItemIcon>
            )
          ) : null}

          <MenuItemTextMetaWrapper
            sx={{
              ...(isSubToSub ? { ml: 8 } : {}),
              ...(navCollapsed && !navHover ? { opacity: 0 } : { opacity: 1 }),
              ...(parent && !item.icon ? { ml: 0 } : {})
            }}
          >
            <Typography
              {...((themeConfig.menuTextTruncate || (!themeConfig.menuTextTruncate && navCollapsed && !navHover)) && {
                noWrap: true
              })}
            >
              <Translations text={item.title} />
            </Typography>
            {item.badgeContent ? (
              <Chip
                size='small'
                label={item.badgeContent}
                color={item.badgeColor || 'primary'}
                sx={{ '& .MuiChip-label': { px: 2.5, lineHeight: 1.385, textTransform: 'capitalize' } }}
              />
            ) : null}
          </MenuItemTextMetaWrapper>
        </MenuNavLink>
      </ListItem>
    </CanViewNavLink>
  )
}

export default VerticalNavLink
